import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";

const statuses = ["pending","confirmed","ready","completed","cancelled"];

export default function OrdersTab({ orders, API, authHeaders, onReload }) {
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const updateOrderStatus = async (oid, status, orderObj=null) => {
    if (status === "cancelled" && orderObj) {
      setCancelTarget(orderObj);
      return;
    }
    await axios.patch(`${API}/orders/${oid}/status`, { status }, { headers: authHeaders() });
    toast.success("Status updated"); onReload();
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await axios.patch(`${API}/orders/${cancelTarget.order_id}/status`, { status: "cancelled" }, { headers: authHeaders() });
      toast.success(`Order #${cancelTarget.order_id} cancelled`);
      setCancelTarget(null);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    }
    setCancelling(false);
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Link to="/admin/picklist" className="btn-primary inline-flex items-center gap-2" data-testid="open-picklist-btn"><Printer size={16}/> PICK LIST / PRINT</Link>
      </div>
      <div className="space-y-3" data-testid="admin-orders">
        {orders.map(o => (
          <div key={o.order_id} className="card-industrial p-5" data-testid={`admin-order-${o.order_id}`}>
            <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
              <div>
                <div className="font-mono text-xs">#{o.order_id}</div>
                <div className="font-display text-lg">{o.customer_name} · {o.customer_phone}</div>
                <div className="font-mono text-xs text-[var(--ink-2)]">{new Date(o.created_at).toLocaleString()} · {o.payment_method}</div>
              </div>
              <div className="font-display text-2xl">${o.total.toFixed(2)}</div>
            </div>
            <div className="font-mono text-sm mb-3">{o.items.map(i=>`${i.name} ×${i.qty}`).join(" · ")}</div>
            {o.notes && <div className="font-mono text-xs italic mb-2">Note: {o.notes}</div>}
            <select value={o.status} onChange={e=>updateOrderStatus(o.order_id, e.target.value, o)} className="input-field max-w-xs" data-testid={`status-${o.order_id}`}>
              {statuses.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
          </div>
        ))}
        {orders.length === 0 && <div className="text-center py-20 font-mono">No orders yet.</div>}
      </div>
      {cancelTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={()=>!cancelling && setCancelTarget(null)} data-testid="cancel-confirm-modal">
          <div className="bg-[var(--paper)] border-2 border-[var(--ink)] max-w-md w-full" onClick={e=>e.stopPropagation()}>
            <div className="p-5 border-b-2 border-[var(--ink)] flex items-center gap-3">
              <div className="bg-[var(--accent)] text-white w-10 h-10 flex items-center justify-center border-2 border-[var(--ink)] font-display">!</div>
              <h2 className="font-display text-xl">CANCEL ORDER?</h2>
            </div>
            <div className="p-5">
              <p className="font-mono text-sm mb-3">You're about to cancel:</p>
              <div className="bg-[var(--muted)] border-2 border-[var(--ink)] p-3 mb-4">
                <div className="font-display text-base mb-1">Order #{cancelTarget.order_id}</div>
                <div className="font-mono text-xs mb-1">{cancelTarget.customer_name} · {cancelTarget.customer_phone}</div>
                <div className="font-mono text-xs">Total: <strong>${cancelTarget.total.toFixed(2)}</strong> · {cancelTarget.payment_method}</div>
              </div>
              {cancelTarget.payment_status === "paid" && (
                <p className="font-mono text-xs bg-[var(--accent-2)] border-2 border-[var(--ink)] p-2 mb-2">⚠ This order was PAID via Square. Cancelling here does not auto-refund — issue the refund from your Square Dashboard.</p>
              )}
              <p className="font-mono text-xs text-[var(--ink-2)]">The customer will see this order as cancelled in their account.</p>
            </div>
            <div className="p-5 border-t-2 border-[var(--ink)] flex gap-3 justify-end">
              <button onClick={()=>setCancelTarget(null)} disabled={cancelling} className="btn-outline" data-testid="cancel-no-btn">NO, KEEP ORDER</button>
              <button onClick={confirmCancel} disabled={cancelling} className="btn-primary bg-[var(--accent)] border-[var(--accent)]" data-testid="cancel-yes-btn">{cancelling?"CANCELLING...":"YES, CANCEL"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
